// Field-level readings of inbound documents, and what accepting one does.
//
// `extract-document-fields` reads a filed PDF and writes one row per field to
// `document_extractions`, each a proposal carrying its own confidence and the
// words it was read from. Nothing reaches a client record until a person
// accepts it here.
//
// Accepting is not always a write. Most of what a PPOT says has no column to
// go in — the plan period, the signature date, the frequency as worded — and
// accepting those records that a human read them and agreed. Two fields do
// have a home, and WRITES_TO names them.
//
// The two that look like they should be in that list and are not are the
// ordered hours. A PPOT says what the physician ORDERED;
// `pa_skilled_hours_week` and `pa_pss_hours_week` hold what the State
// AUTHORISED. They are frequently different numbers, the authorised one is what
// billing and the hours panel are built on, and copying the ordered figure over
// it would quietly corrupt both. They are shown side by side so a coordinator
// can see the gap — which is worth seeing — and neither one overwrites the
// other.
import { supabase } from './supabase';

// field_key → the `clients` column accepting it writes to, and how the value
// has to be shaped to fit.
//
// The clinical free-text fields write, which they did not in the first cut.
// The objection then was that a PPOT is a snapshot and is often thinner than
// what a nurse has already written, so accepting one could walk the record
// backwards and lose the fuller text. That objection is answered rather than
// ignored: accepting now reads the live value first and keeps it in
// `replaced_value` on the same row, dated by `effective_date`, so a field's
// whole history is its accepted rows and nothing is destroyed. If a PPOT turns
// out to be worse than what was there, the old text is one click away.
//
// Three fields still do not write, because they have no column to write to:
// the plan period and the signature date belong to the document, not the child.
export const WRITES_TO = {
  physician_name:         { column: 'ordering_physician_name', as: 'text' },
  physician_npi:          { column: 'ordering_physician_npi',  as: 'text' },

  primary_diagnosis:      { column: 'diagnosis_summary',   as: 'text' },
  full_diagnoses:         { column: 'full_diagnoses',      as: 'text' },
  diagnosis_codes:        { column: 'diagnosis_code',      as: 'text' },
  current_medications:    { column: 'current_medications', as: 'text' },
  diet_description:       { column: 'diet_description',    as: 'text' },
  allergies:              { column: 'allergies',           as: 'text' },
  hospitalizations:       { column: 'hospitalizations',    as: 'text' },
  weight_lbs:             { column: 'weight_lbs',          as: 'number' },

  medical_equipment:      { column: 'medical_equipment',   as: 'array' },
  therapies:              { column: 'therapies',           as: 'array' },
  assistance_needed:      { column: 'assistance_needed',   as: 'array' },

  flag_g_tube:            { column: 'g_tube',             as: 'boolean' },
  flag_uses_oxygen:       { column: 'uses_oxygen',        as: 'boolean' },
  flag_seizures:          { column: 'seizures',           as: 'boolean' },
  flag_swallowing_issues: { column: 'swallowing_issues',  as: 'boolean' },
  flag_contractures:      { column: 'contractures',       as: 'boolean' },
  flag_diapers:           { column: 'diapers',            as: 'boolean' },
  flag_potty_trained:     { column: 'potty_trained',      as: 'boolean' },
};

// The two hours fields are deliberately absent and must stay absent. A PPOT
// says what the physician ORDERED; pa_skilled_hours_week and pa_pss_hours_week
// hold what the State AUTHORISED. Billing, the hours panel and the projections
// are all built on the authorised figure, and this is not a versioning problem
// — the two numbers mean different things, so one is never the other's history.

const shape = (value, as) => {
  const v = String(value ?? '').trim();
  if (!v) return null;
  if (as === 'number') { const n = parseFloat(v.replace(/[^\d.]/g, '')); return Number.isFinite(n) ? n : null; }
  if (as === 'boolean') return /^(y|yes|true|1)$/i.test(v);
  if (as === 'array') return v.split(',').map((x) => x.trim()).filter(Boolean);
  return v;
};

/** Below this, a field is shown but not offered for one-click acceptance. */
export const LOW_CONFIDENCE = 0.6;

export const CONFIDENCE_LABEL = (c) =>
  c >= 0.85 ? 'high' : c >= LOW_CONFIDENCE ? 'fair' : c > 0 ? 'low' : 'not found';

/** Every proposal for a client, newest document first, grouped by fax. */
export async function getClientExtractions(clientId) {
  const { data, error } = await supabase
    .from('document_extractions')
    .select('*, fax:faxes(id, file_name, doc_type, sender_name, received_at, drive_url, fields_extract_error)')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) return { data: null, error };

  const byFax = new Map();
  for (const row of data || []) {
    const id = row.fax_id;
    if (!byFax.has(id)) byFax.set(id, { fax: row.fax, fields: [] });
    byFax.get(id).fields.push(row);
  }
  // Within a document, keep the order the extractor defined rather than
  // whatever the query returned, so the reviewer reads a form top to bottom.
  const ORDER = ['Plan', 'Physician', 'Clinical', 'Medical flags'];
  const groups = [...byFax.values()];
  for (const g of groups) {
    g.pending = g.fields.filter((f) => f.status === 'proposed' && f.extracted_value).length;
    // 26 fields is too many to read as one list, so they are shown by the part
    // of the form they came from, in the order the form presents them.
    const by = new Map();
    for (const f of g.fields) {
      const key = f.field_group || 'Other';
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(f);
    }
    g.sections = [...by.entries()]
      .sort((a, b) => ORDER.indexOf(a[0]) - ORDER.indexOf(b[0]))
      .map(([name, fields]) => ({
        name,
        fields,
        found: fields.filter((f) => f.extracted_value).length,
      }));
  }
  groups.sort((a, b) => String(b.fax?.received_at || '').localeCompare(String(a.fax?.received_at || '')));
  return { data: groups, error: null };
}

/** How many fields across a client are waiting to be looked at. */
export async function countPendingExtractions(clientId) {
  const { count } = await supabase
    .from('document_extractions')
    .select('id', { count: 'exact', head: true })
    .eq('client_id', clientId)
    .eq('status', 'proposed')
    .not('extracted_value', 'is', null);
  return count || 0;
}

/**
 * Accept a field, optionally with a corrected value.
 *
 * `edited` is kept distinct from `accepted` on purpose: the gap between what
 * was extracted and what a person settled on is the only honest measure of
 * whether the reading is any good, and collapsing the two throws it away.
 */
export async function acceptField(row, value, profile) {
  const finalValue = value ?? row.extracted_value;
  const edited = String(finalValue ?? '') !== String(row.extracted_value ?? '');
  const target = WRITES_TO[row.field_key];
  const shaped = target ? shape(finalValue, target.as) : null;

  // Read the live value now rather than trusting `current_value`, which was
  // taken when the document was read and may be stale — a record can be edited
  // between reading a document and confirming it, and the value that was
  // actually replaced is the one worth keeping.
  let replaced = null;
  if (target && row.client_id && shaped !== null) {
    const { data } = await supabase.from('clients').select(target.column).eq('id', row.client_id).single();
    const was = data?.[target.column];
    replaced = was == null ? null : (Array.isArray(was) ? was.join(', ') : String(was));
  }

  const { error } = await supabase.from('document_extractions').update({
    status: edited ? 'edited' : 'accepted',
    accepted_value: finalValue,
    replaced_value: replaced,
    reviewed_by: profile?.id || null,
    reviewed_by_name: profile?.full_name || null,
    reviewed_at: new Date().toISOString(),
  }).eq('id', row.id);
  if (error) return { error };

  if (target && row.client_id && shaped !== null) {
    const { error: cErr } = await supabase.from('clients')
      .update({ [target.column]: shaped }).eq('id', row.client_id);
    if (cErr) return { error: cErr };
    return { error: null, wroteTo: target.column, replaced };
  }
  return { error: null, wroteTo: null };
}

/**
 * What this field has been, newest first — its accepted readings, each dated by
 * the document that said it and carrying what it replaced.
 */
export async function getFieldHistory(clientId, fieldKey) {
  const { data, error } = await supabase
    .from('document_extractions')
    .select('accepted_value, replaced_value, effective_date, reviewed_at, reviewed_by_name, status')
    .eq('client_id', clientId)
    .eq('field_key', fieldKey)
    .in('status', ['accepted', 'edited'])
    .order('effective_date', { ascending: false, nullsFirst: false });
  return { data, error };
}

export async function rejectField(row, profile) {
  const { error } = await supabase.from('document_extractions').update({
    status: 'rejected',
    reviewed_by: profile?.id || null,
    reviewed_by_name: profile?.full_name || null,
    reviewed_at: new Date().toISOString(),
  }).eq('id', row.id);
  return { error };
}

/** Read (or re-read) a filed document. */
export async function extractFax(faxId) {
  const { data, error } = await supabase.functions.invoke('extract-document-fields', {
    body: { fax_id: faxId },
  });
  if (error) {
    let message = error.message;
    try { const b = await error?.context?.json?.(); if (b?.error) message = b.error; } catch { /* keep the original */ }
    return { data: null, error: new Error(message) };
  }
  return { data, error: null };
}

/** Inbound documents for this client that have a type we can read. */
export async function getReadableFaxes(clientId) {
  const { data, error } = await supabase
    .from('faxes')
    .select('id, file_name, doc_type, sender_name, received_at, drive_url, fields_extracted_at, fields_extract_error')
    .eq('client_id', clientId)
    .eq('direction', 'inbound')
    .in('doc_type', ['ppot'])
    .order('received_at', { ascending: false });
  return { data, error };
}
